import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntitySearchList } from "@/components/ui/entity-search-list";
import { searchRegistrantAccountsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type { ImpersonationRegistrantSearchItemSchema } from "@/hera-api/client/types.gen";
import { useDebounce } from "@/hooks/use-debounce";
import { getQueryErrorMessage } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

function registrantSelectionSummary(
  registrant: ImpersonationRegistrantSearchItemSchema,
): string {
  const details = [registrant.name, registrant.company_name].filter(
    (detail): detail is string => Boolean(detail),
  );
  const prefix = details.length > 0 ? `${details.join(" · ")}. ` : "";
  return `${prefix}Edit the search to pick a different account.`;
}

interface EditLicenseRegistrantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentRegistrantId?: string;
  onSave: (
    registrant: ImpersonationRegistrantSearchItemSchema,
  ) => Promise<void>;
  isSaving: boolean;
  elevated?: boolean;
}

export function EditLicenseRegistrantDialog({
  open,
  onOpenChange,
  currentRegistrantId,
  onSave,
  isSaving,
  elevated = false,
}: EditLicenseRegistrantDialogProps) {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query.trim(), 300);
  const [selected, setSelected] =
    useState<ImpersonationRegistrantSearchItemSchema | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelected(null);
    }
  }, [open]);

  const { data, isFetching, error } = useQuery({
    ...searchRegistrantAccountsOptions({
      query: { q: debouncedQuery, limit: 25 },
    }),
    // A picked registrant puts its email in the input; searching for that email
    // again would only re-list the account already chosen.
    enabled: open && selected === null && debouncedQuery.length >= 2,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent elevated={elevated}>
        <DialogHeader>
          <DialogTitle>Edit registrant</DialogTitle>
          <DialogDescription>
            Correct the registrant assigned to this license. This does not
            transfer the license or change its status, payments, dates, or
            transfer lineage.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <EntitySearchList
            query={query}
            onQueryChange={(nextQuery) => {
              setQuery(nextQuery);
              setSelected(null);
            }}
            results={(data?.registrants ?? []).map((registrant) => {
              const isCurrent = registrant.id === currentRegistrantId;
              return {
                value: registrant.id,
                primary: registrant.email,
                secondary: registrant.name,
                description: registrant.company_name,
                note: isCurrent ? "Current registrant" : null,
                disabled: isCurrent,
              };
            })}
            onSelect={(id) => {
              const registrant = data?.registrants.find(
                (item) => item.id === id,
              );
              if (!registrant) return;
              setSelected(registrant);
              setQuery(registrant.email);
            }}
            placeholder="Search by email, name, or company…"
            ariaLabel="Search registrants"
            emptyMessage="No registrants match."
            isFetching={isFetching}
            errorMessage={error ? getQueryErrorMessage(error) : null}
            showResults={selected === null}
          />
          {selected ? (
            <p className="text-sm text-muted-foreground">
              {registrantSelectionSummary(selected)}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isSaving}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!selected || isSaving}
            onClick={() => selected && void onSave(selected)}
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
